import { useEffect, useRef, useState } from 'react';
import WatchEventDetails from '../../components/WatchEventDetails.jsx';
import WatchGroupLinks from '../../components/WatchGroupLinks.jsx';
import PublicWatchShell from '../../components/PublicWatchShell.jsx';
import PageState from '../../components/PageState.jsx';
import SectionCard from '../../components/SectionCard.jsx';
import Notice from '../../components/Notice.jsx';
import StatusWord from '../../components/StatusWord.jsx';
import WatchAnswers from '../../components/WatchAnswers.jsx';
import { postPublic } from '../../lib/watch-public-api.js';
import { takeFragment } from '../../lib/watch-fragment.js';
import { WATCH_QUESTION_DEFAULT } from '../../../../shared/watch-question.mjs';
import { SEVERITY_LABEL, SEVERITY_TONE, formatWhen } from '../../lib/watch-format.js';

// Plain wording that says what happened and what to do, and never offers a login. Before the token is
// known valid the server answers generically ("invalid"); after it, it names the state.
const MESSAGES = {
  invalid: 'This link is not valid.',
  answered: 'This notice was already answered.',
  expired: 'This link has expired. Ask your onGuard administrator to review the notice in the onGuard portal.',
  revoked: 'This link no longer works.',
  busy: 'Too many attempts. Try again later.',
  offline: 'The onGuard portal could not be reached. Try again later.',
  'event-missing': 'The event in this notice is no longer available, so it cannot be answered here. Ask your onGuard administrator to review it in the onGuard portal.',
  'cannot-answer': 'This copy of the notice is for your information only, so it does not take an answer.',
  failed: 'Your answer could not be recorded because the request was incomplete. Open the link in your email again. If this happens again, contact your onGuard administrator.',
};
const RETRY_MESSAGE = 'Your answer could not be recorded. Try again.';
// The form is valid for 30 minutes after the page opens (FORM_NONCE_TTL_MS). Past that, the page is
// refreshed in place and the person confirms again: their answer and comment are kept.
const REFRESHED_MESSAGE = 'This page was open for more than 30 minutes, so it was refreshed. Your answer is kept. Check it and press Confirm again.';
const NAMED_REFUSALS = new Set(['event-missing', 'cannot-answer']);

const stateOf = ({ status, body }) => {
  if (status === 200 && body?.state) return body.state;
  if (status === 409) return 'answered';
  if (status === 410) return body?.state === 'revoked' ? 'revoked' : 'expired';
  if (status === 429) return 'busy';
  if (status === 404 && body?.state === 'event-missing') return 'event-missing';
  return 'invalid';
};

export default function WatchReview() {
  const token = useRef(null);
  const started = useRef(false);
  const [phase, setPhase] = useState('loading');
  const [view, setView] = useState(null);
  const [nonce, setNonce] = useState(null);
  const [answer, setAnswer] = useState('');
  const [comment, setComment] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  // The fragment is read and removed from the address bar here, before the
  // first call, and only once (StrictMode runs effects twice in development).
  // A page load only views; nothing is answered without the Confirm below.
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const frag = takeFragment(window);
    token.current = frag.t ?? null;
    if (!frag.t) { setPhase('invalid'); return; }
    if (frag.a) setAnswer(frag.a);
    postPublic('/watch/links/view', { token: frag.t })
      .then((r) => {
        const s = stateOf(r);
        if (s === 'ready' && r.body.view && r.body.formNonce) {
          setView(r.body.view);
          setNonce(r.body.formNonce);
          setPhase('ready');
        } else {
          setPhase(s === 'ready' ? 'invalid' : s);
        }
      })
      .catch(() => setPhase('offline'));
  }, []);

  // A fresh view gives a fresh form nonce; nothing is answered until Confirm is pressed again.
  async function refresh() {
    const v = await postPublic('/watch/links/view', { token: token.current });
    const s = stateOf(v);
    if (s === 'ready' && v.body.view && v.body.formNonce) {
      setView(v.body.view);
      setNonce(v.body.formNonce);
      setNotice(REFRESHED_MESSAGE);
    } else {
      setPhase(s === 'ready' ? 'invalid' : s);
    }
  }

  async function confirm(e) {
    e.preventDefault();
    if (!answer || busy) return;
    setBusy(true);
    setNotice(null);
    try {
      const body = { token: token.current, answer, formNonce: nonce, ...(answer === 'no' && comment.trim() ? { comment: comment.trim() } : {}) };
      const r = await postPublic('/watch/links/attest', body);
      if (r.status === 200 && r.body?.state === 'recorded') setResult(r.body);
      else if (r.body?.state === 'page-expired') await refresh();
      else if (NAMED_REFUSALS.has(r.body?.state)) setPhase(r.body.state);
      else if (r.status === 409 || r.status === 410) setPhase(stateOf(r));
      else if (r.status === 429) setNotice(MESSAGES.busy);
      else if (r.status >= 500) setNotice(RETRY_MESSAGE);
      else setPhase('failed');
    } catch {
      setNotice(RETRY_MESSAGE);
    } finally {
      setBusy(false);
    }
  }

  let content;
  if (phase === 'loading') content = <PageState state="loading" cards={0} label="Opening your link" />;
  else if (phase !== 'ready') content = <Notice tone={['busy', 'offline', 'cannot-answer'].includes(phase) ? 'warn' : 'bad'} titleAs="h1" title={MESSAGES[phase] ?? MESSAGES.invalid} />;
  else {
    content = (
      <>
        <SectionCard ariaLabel="The change" bodyClassName="space-y-3">
          <span className={SEVERITY_TONE[view.severity] ?? 'badge-slate'}>{SEVERITY_LABEL[view.severity] ?? view.severity}</span>
          <h1 className="text-xl break-words">{view.tenantName}: {view.what}</h1>
          <dl className="space-y-2 text-sm">
            <div><dt className="text-ipc-muted">Who</dt><dd className="break-words">{view.who}</dd></div>
            <div><dt className="text-ipc-muted">When</dt><dd>{formatWhen(view.when)}</dd></div>
            <div><dt className="text-ipc-muted">Why you received it</dt><dd>{view.reason}</dd></div>
            <div><dt className="text-ipc-muted">How it was detected</dt><dd>{view.detected}</dd></div>
          </dl>
          <WatchGroupLinks links={view.groupLinks} />
          <WatchEventDetails details={view.details} />
        </SectionCard>
        {view.canAnswer ? (
          <SectionCard ariaLabel="Your answer" bodyClassName="space-y-3">
            <WatchAnswers answers={view.answers} resolved={view.resolved} />
            <h2 className="text-lg">{(view.question ?? WATCH_QUESTION_DEFAULT).question}</h2>
            {result ? (
              <div role="status" className="space-y-1">
                <p><StatusWord tone="good">Recorded</StatusWord></p>
                {result.answer === 'no' ? <p className="text-sm text-ipc-ink">Your security team has been told</p> : null}
              </div>
            ) : (
              <form className="space-y-3" onSubmit={confirm}>
                <div className="flex gap-6">
                  {['yes', 'no'].map((v) => (
                    <label key={v} className="flex items-center gap-2 text-sm">
                      <input type="radio" name="answer" value={v} checked={answer === v} onChange={() => setAnswer(v)} />
                      {(view.question ?? WATCH_QUESTION_DEFAULT)[v]}
                    </label>
                  ))}
                </div>
                {answer === 'no' ? (
                  <label className="block text-sm">
                    <span className="label">Comment (optional)</span>
                    <textarea className="input" maxLength={500} value={comment} onChange={(e) => setComment(e.target.value)} />
                  </label>
                ) : null}
                {notice ? <p role="alert" className="text-sm"><StatusWord tone={notice === REFRESHED_MESSAGE ? 'warn' : 'bad'} strong={false}>{notice}</StatusWord></p> : null}
                <button type="submit" className="btn-primary w-full sm:w-auto" disabled={!answer || busy}>Confirm</button>
              </form>
            )}
          </SectionCard>
        ) : null}
      </>
    );
  }
  return <PublicWatchShell>{content}</PublicWatchShell>;
}
